import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import '../../assets/styles/account/account.scss';
import usePageMeta from '../../services/page-meta';

const ACTIVATION_CODE_LENGTH = 6;

function formatActivationCode(value) {
    const normalized = (value || '')
        .replace(/[^A-Za-z0-9]/g, '')
        .toUpperCase()
        .slice(0, ACTIVATION_CODE_LENGTH);

    if (normalized.length <= 2) {
        return normalized;
    }

    if (normalized.length <= 4) {
        return `${normalized.slice(0, 2)}-${normalized.slice(2)}`;
    }

    return `${normalized.slice(0, 2)}-${normalized.slice(2, 4)}-${normalized.slice(4)}`;
}

function replaceAttempts(template, attemptsLeft) {
    return (template || '').replace('attemptsLeft', String(attemptsLeft));
}

export default function InactivePage() {
    const {t} = useTranslation();
    usePageMeta({
        title: t('meta.inactive.title', t('app.title')),
        description: t('meta.inactive.description', t('app.description')),
        keywords: t('meta.inactive.keywords', t('app.keywords')),
    });

    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [emailAttemptsLeft, setEmailAttemptsLeft] = useState(null);
    const [isEmailResentVisible, setIsEmailResentVisible] = useState(false);
    const [isEmailResendDisabled, setIsEmailResendDisabled] = useState(false);

    const [smsCode, setSmsCode] = useState('');
    const [hasSmsError, setHasSmsError] = useState(false);
    const [isSmsSubmitting, setIsSmsSubmitting] = useState(false);
    const [smsSentText, setSmsSentText] = useState('');
    const [smsResendMessage, setSmsResendMessage] = useState('');
    const [showNoSmsCaption, setShowNoSmsCaption] = useState(false);

    useEffect(() => {
        let isCancelled = false;

        fetch('/api/v1/inactive', {credentials: 'same-origin'})
            .then(async (response) => {
                let data = {};
                try {
                    data = await response.json();
                } catch (error) {
                    data = {};
                }

                if (!response.ok) {
                    throw data;
                }

                return data;
            })
            .then((data) => {
                if (isCancelled) {
                    return;
                }

                if (data?.state === 'redirect' && data?.redirectUrl) {
                    window.location.href = data.redirectUrl;
                    return;
                }

                setPayload(data);
            })
            .catch((errorPayload) => {
                if (isCancelled) {
                    return;
                }

                const redirectUrl = errorPayload?.redirectUrl;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }

                setPayload({state: 'error'});
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, []);

    const emailSentCaption = useMemo(
        () => t('inactiveByEmail.emailSentTo').replace('%email%', payload?.email || ''),
        [payload?.email, t]
    );

    const handleEmailResend = (event) => {
        event.preventDefault();
        if (isEmailResendDisabled) {
            return;
        }

        const resendUrl = payload?.actions?.resendEmailUrl || '/api/v1/account/activate/email/resend';

        fetch(resendUrl, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('no_attempts');
                }

                const data = await response.json();
                const attemptsLeft = Number(data?.attemptsLeft || 0);

                setIsEmailResentVisible(true);
                window.setTimeout(() => {
                    setIsEmailResentVisible(false);
                }, 3000);

                if (attemptsLeft > 0) {
                    setEmailAttemptsLeft(attemptsLeft);
                    return;
                }

                setIsEmailResendDisabled(true);
                setEmailAttemptsLeft(null);
            })
            .catch(() => {
                setIsEmailResendDisabled(true);
                setEmailAttemptsLeft(null);
            });
    };

    const handleSmsSubmit = (event) => {
        event.preventDefault();
        if (isSmsSubmitting) {
            return;
        }

        const activateSmsUrl = payload?.actions?.activateSmsUrl || '/api/v1/account/activate/sms';
        const code = smsCode.replace(/[^A-Za-z0-9]/g, '');
        const formData = new FormData();
        formData.append('code', code);

        setIsSmsSubmitting(true);
        setHasSmsError(false);

        fetch(activateSmsUrl, {
            method: 'POST',
            credentials: 'same-origin',
            body: formData,
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('invalid_code');
                }

                const data = await response.json();
                window.location.href = data?.url || '/';
            })
            .catch(() => {
                setHasSmsError(true);
            })
            .finally(() => {
                setIsSmsSubmitting(false);
            });
    };

    const handleSmsResend = (event) => {
        event.preventDefault();

        const resendSmsUrl = payload?.actions?.resendSmsUrl || '/api/v1/account/activate/sms/resend';

        fetch(resendSmsUrl, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('resend_failed');
                }

                const data = await response.json();
                const attemptsLeft = Number(data?.attemptsLeft || 0);

                setSmsSentText(t('activation.smsResent'));
                setShowNoSmsCaption(true);

                if (attemptsLeft > 0) {
                    setSmsResendMessage(
                        replaceAttempts(t('activation.attemptsSmsLeft'), attemptsLeft)
                    );
                    return;
                }

                setSmsResendMessage(t('activation.resendInOneDay'));
            })
            .catch(() => {
                setShowNoSmsCaption(true);
                setSmsResendMessage(t('activation.resendInOneDay'));
            });
    };

    if (isLoading) {
        return <section className="section flex-center" />;
    }

    if (payload?.state === 'phone') {
        return (
            <section className="section flex-center">
                <div className="authentication-form">
                    <div className="restore-by-phone">
                        <h1>{t('inactiveByPhone.header')}</h1>
                        <div className="sent-text">
                            <span
                                className="js-sms-sent-text"
                                dangerouslySetInnerHTML={{
                                    __html: smsSentText || t('inactiveByPhone.forConfirmationCaption')
                                }}
                            />
                            {' '}
                            <span className="user-phone">{payload?.phone || ''}</span>
                        </div>
                        <h5>{t('inactiveByPhone.enterCode')}</h5>
                        <form id="smsConfirmation" className="form" onSubmit={handleSmsSubmit}>
                            <div className="form-group">
                                <input
                                    id="code"
                                    name="code"
                                    type="text"
                                    value={smsCode}
                                    onChange={(event) => setSmsCode(formatActivationCode(event.target.value))}
                                    placeholder="XX-XX-XX"
                                    className="form form-code"
                                    autoComplete="off"
                                />
                                <div className={`error_text js-error${hasSmsError ? '' : ' hidden'}`}>
                                    <span>{t('inactiveByPhone.incorrectCode')}</span>
                                </div>
                            </div>
                            <div className="not-received-code">
                                <a href="/" className="js-resend-sms" onClick={handleSmsResend}>
                                    {t('inactiveByPhone.didntReceiveSms')}
                                </a>
                                <p className={`js-resend-error${smsResendMessage ? '' : ' hidden'}`}>
                                    {smsResendMessage}
                                </p>
                            </div>
                            <div className="submit-block">
                                <input
                                    type="submit"
                                    value={t('inactiveByPhone.confirm')}
                                    className="btn"
                                    disabled={isSmsSubmitting}
                                />
                            </div>
                            <div className={`no-sms-block js-no-sms-block${showNoSmsCaption ? '' : ' hidden'}`}>
                                {t('inactiveByPhone.ifNoSmsCaption')}
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        );
    }

    if (payload?.state !== 'email') {
        return <section className="section flex-center" />;
    }

    return (
        <section className="section flex-center">
            <div className="authentication-form regVerify">
                <h1>{t('inactiveByEmail.header')}</h1>
                <div>
                    <p>{emailSentCaption}</p>
                    <p><strong>{t('inactiveByEmail.checkYourMail')}</strong></p>
                    <p>{t('inactiveByEmail.checkSpam')}</p>
                </div>
                <div className="resend-block">
                    <div className="text-resended-block">
                        <span className={`js-text-resended${isEmailResentVisible ? '' : ' hidden'}`}>
                            {t('inactiveByEmail.emailResent')}
                        </span>
                    </div>
                    <div className={`no-attempt-texts js-no-attempt-texts${isEmailResendDisabled ? '' : ' hidden'}`}>
                        {t('inactiveByEmail.noAttempts')}
                    </div>
                    <div className="resend-email-block js-resend-email">
                        {!isEmailResendDisabled && (
                            <a className="resent-link" href="/" onClick={handleEmailResend}>
                                {t('inactiveByEmail.resend')}
                            </a>
                        )}
                    </div>
                    <div className="number-of-attempts-block">
                        <span className={`js-text-attempts${emailAttemptsLeft ? '' : ' hidden'}`}>
                            {emailAttemptsLeft
                                ? replaceAttempts(t('activation.attemptsLeft'), emailAttemptsLeft)
                                : ''}
                        </span>
                    </div>
                    <p
                        className="description-text"
                        dangerouslySetInnerHTML={{__html: t('inactiveByEmail.noEmailCaption')}}
                    />
                </div>
            </div>
        </section>
    );
}
